/**
 * How to lay out a yantra board of any size.
 *
 * The boards used to be exactly ten tiles in a fixed 5x2 grid. A game can
 * now carry anywhere from 2 to 16, so the column count is computed rather
 * than written into the CSS - one function here instead of a rule per
 * size scattered across three screens.
 *
 * On desktop every board sits in exactly two rows (5x2, 6x2 ... 8x2), so
 * the operator's eye always finds a yantra in the same place. Phones and
 * tablets fall back to more rows because the tiles must stay tappable.
 */

/** Columns for a board of `count` tiles at a given breakpoint. */
export function getBoardColumns(count, viewport = "desktop") {
  const n = Math.max(0, Number(count) || 0);
  if (!n) return 1;

  if (viewport === "mobile") {
    // A phone can hold three tiles across before they stop being tappable.
    if (n <= 4) return 2;
    return 3;
  }

  if (viewport === "tablet") {
    if (n <= 4) return 2;
    if (n <= 12) return 4;
    return 5;
  }

  // Desktop: always two rows. A 16-ghar board is 8 across with smaller tiles.
  if (n <= 3) return n;
  return Math.ceil(n / 2);
}

/**
 * Tile size for a given column count.
 *
 * Up to five across, the original 120px artwork has room and is left
 * exactly as it was. Past that the tiles get narrow, so the image and the
 * caption shrink. The grid row already stretches every card to the same
 * height, so no space is reserved for a second caption line.
 */
function getTileSizing(columns) {
  if (columns <= 5) return null; // untouched: the ten-ghar board
  if (columns === 6) return { img: "92px", font: "13px" };
  if (columns === 7) return { img: "80px", font: "12px" };
  return { img: "72px", font: "11.5px" };
}

/**
 * A ready-to-use grid style. Using an inline style rather than a class per
 * size keeps every board size working without touching CSS again.
 *
 * The custom properties are read by the card CSS and fall back to the
 * original values when they are absent, so a five-column board renders
 * byte-for-byte as before.
 */
export function getBoardGridStyle(count, viewport = "desktop") {
  const columns = getBoardColumns(count, viewport);
  const tile = getTileSizing(columns);

  return {
    display: "grid",
    gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
    ...(tile
      ? {
          "--yantra-img": tile.img,
          "--yantra-name-size": tile.font,
        }
      : {}),
  };
}

/**
 * The outlet screen sizes its tiles from the viewport height (see
 * --yantra-icon-fit in OutletLiveSession.css). Five columns always have the
 * width for that size; six or more may not, so the icon is also capped by
 * the column width: the grid is the viewport minus the 351px side panel and
 * page padding, split into columns with 12px gaps and 8px card padding.
 */
function getOutletTileSizing(columns) {
  if (columns <= 5) return null;
  const widthCap = `calc((100vw - 400px - ${columns - 1} * 12px) / ${columns} - 20px)`;
  return {
    icon: `max(56px, min(var(--yantra-icon-fit), ${widthCap}))`,
    font: columns === 6 ? "16px" : columns === 7 ? "15px" : "14px",
    // Past twelve ghar the cards are narrow enough that a 75% count box is
    // hard to read and to hit, so it takes the full card width.
    inputWidth: columns >= 7 ? "100%" : null,
  };
}

/** Grid style for the outlet board; same column rule, outlet's own CSS vars. */
export function getOutletBoardGridStyle(count, viewport = "desktop") {
  const columns = getBoardColumns(count, viewport);
  const tile = getOutletTileSizing(columns);

  return {
    gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
    ...(tile
      ? {
          "--yantra-icon-size": tile.icon,
          "--yantra-name-size": tile.font,
          ...(tile.inputWidth ? { "--yantra-input-width": tile.inputWidth } : {}),
        }
      : {}),
  };
}

/** Breakpoint name for a viewport width, matching the CSS media queries. */
export function viewportFor(width) {
  const w = Number(width) || 0;
  if (w <= 768) return "mobile";
  if (w <= 1024) return "tablet";
  return "desktop";
}

/**
 * Every tile priced at the round's bet unit.
 *
 * The board list carries the game's current price; the round in play may
 * have been generated before an edit, and the server charges the round's
 * price, so the screen must show that one. No-op when the round has none.
 */
export function repriceYantraList(list, unitPrice) {
  const price = Number(unitPrice);
  if (!Array.isArray(list) || !Number.isFinite(price) || price <= 0) return list;
  if (list.every((y) => Number(y?.yantra_amount) === price)) return list;
  return list.map((y) => ({ ...y, yantra_amount: price }));
}
